import React, { Suspense, lazy, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Phone,
  Network,
  Workflow,
  Users2,
  Headphones,
  Menu,
  Mic,
  MessageSquare,
  Clock,
  Settings2
} from 'lucide-react';
import { HubShell, type HubTab } from '@/components/common/HubShell';

const Extensions = lazy(() => import('./Extensions'));
const SipTrunks = lazy(() => import('./SipTrunks'));
const CallRouting = lazy(() => import('./CallRouting'));
const DialplanDebug = lazy(() => import('./DialplanDebug'));
const RingGroups = lazy(() => import('./RingGroups'));
const Queues = lazy(() => import('./Queues'));
const IvrMenus = lazy(() => import('./IvrMenus'));
const AudioManager = lazy(() => import('./AudioManager'));
const ConferenceRooms = lazy(() => import('./ConferenceRooms'));
const Voicemail = lazy(() => import('./Voicemail'));
const TimeConditions = lazy(() => import('./TimeConditions'));

type TabKey =
  | 'extensions'
  | 'sip-trunks'
  | 'call-routing'
  | 'dialplan-debug'
  | 'ring-groups'
  | 'queues'
  | 'ivr-menus'
  | 'audio-manager'
  | 'conference-rooms'
  | 'voicemail'
  | 'time-conditions';

const tabs: readonly HubTab<TabKey>[] = [
  { key: 'extensions', label: 'Extensions', icon: Phone },
  { key: 'sip-trunks', label: 'SIP Trunks', icon: Network },
  { key: 'call-routing', label: 'Call Routing', icon: Network },
  { key: 'dialplan-debug', label: 'Dialplan Debug', icon: Workflow },
  { key: 'ring-groups', label: 'Ring Groups', icon: Users2 },
  { key: 'queues', label: 'Call Queues', icon: Headphones },
  { key: 'ivr-menus', label: 'IVR Menus', icon: Menu },
  { key: 'audio-manager', label: 'Audio Manager', icon: Mic },
  { key: 'conference-rooms', label: 'Conference Rooms', icon: MessageSquare },
  { key: 'voicemail', label: 'Voicemail', icon: Mic },
  { key: 'time-conditions', label: 'Orari e Festività', icon: Clock },
];

const tabComponents: Record<TabKey, React.LazyExoticComponent<React.ComponentType>> = {
  'extensions': Extensions,
  'sip-trunks': SipTrunks,
  'call-routing': CallRouting,
  'dialplan-debug': DialplanDebug,
  'ring-groups': RingGroups,
  'queues': Queues,
  'ivr-menus': IvrMenus,
  'audio-manager': AudioManager,
  'conference-rooms': ConferenceRooms,
  'voicemail': Voicemail,
  'time-conditions': TimeConditions,
};

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
    </div>
  );
}

export default function VoipPbxManagement() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') || 'extensions';
  const activeTab = tabs.some(t => t.key === rawTab) ? (rawTab as TabKey) : 'extensions';

  const handleTabChange = useCallback((tabKey: TabKey) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tabKey);
    const keysToRemove: string[] = [];
    newParams.forEach((_, key) => {
      if (key !== 'tab') keysToRemove.push(key);
    });
    keysToRemove.forEach(k => newParams.delete(k));
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const ActiveComponent = tabComponents[activeTab] || Extensions;

  return (
    <HubShell
      icon={Settings2}
      title="Gestione PBX"
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      mainClassName="flex-1 overflow-auto"
    >
      <Suspense fallback={<LoadingSpinner />}>
        <ActiveComponent />
      </Suspense>
    </HubShell>
  );
}
